import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, Flag, UserX, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Wraps a user avatar with a tap-to-open popover that exposes:
 *   • Message — opens /messages?with=<user_id>
 *   • Block   — soft-blocks the user (no future content / DMs from either side)
 *   • Report spam — sends a moderation report to the inbox
 *
 * Renders nothing extra for the current user (you can't block yourself).
 * The avatar itself still renders as a normal <img> when isSelf is true.
 *
 *   <UserActionsMenu
 *     targetUserId={p.user_id}
 *     targetName={p.author_name}
 *     targetAvatar={p.author_avatar}
 *     currentUserId={me.id}
 *     contextLabel="post"
 *     contextId={p.id}
 *     onBlocked={() => removePost(p.id)}
 *     imgClassName="w-12 h-12 rounded-full"
 *     testIdPrefix={`lounge-avatar-${p.id}`}
 *   />
 */
export default function UserActionsMenu({
  targetUserId,
  targetName,
  targetAvatar,
  currentUserId,
  contextLabel = "content",  // shown in the report-confirm message
  contextId = null,           // post / comment / message id
  contextType = "post",       // "post" | "comment" | "message" | "user"
  onMessage,                  // override default /messages?with= navigation
  onBlocked,                  // parent can remove the row from the feed
  onReported,
  imgClassName = "w-12 h-12 rounded-full bg-tb-cream-2 object-cover shrink-0",
  imgAlt,
  testIdPrefix = "user-actions",
  align = "left",
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const isSelf = targetUserId && currentUserId && targetUserId === currentUserId;

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const doMessage = () => {
    setOpen(false);
    if (onMessage) return onMessage(targetUserId);
    // Default: navigate to the conversation.
    window.location.assign(`/messages?with=${targetUserId}`);
  };

  const doBlock = async () => {
    setOpen(false);
    if (!window.confirm(
      `Block ${targetName || "this user"}? You won't see each other's stories or messages anymore.`
    )) return;
    setBusy(true);
    try {
      await api.post("/blocks", { target_user_id: targetUserId });
      toast.success(`${targetName || "User"} blocked`);
      onBlocked?.(targetUserId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't block");
    } finally { setBusy(false); }
  };

  const doReport = async () => {
    setOpen(false);
    const reason = window.prompt(
      `Flag this ${contextLabel} for the admin team to review? (optional details — e.g. nudity, harassment, scam)`
    );
    if (reason === null) return; // user cancelled
    setBusy(true);
    try {
      await api.post("/reports", {
        target_type: contextType,
        target_id: contextId || targetUserId,
        target_user_id: targetUserId,
        reason: reason || null,
      });
      toast.success("Flagged — thanks, we'll review it shortly");
      onReported?.(targetUserId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't flag");
    } finally { setBusy(false); }
  };

  if (isSelf) {
    return (
      <img
        src={targetAvatar} alt={imgAlt || targetName || ""}
        className={imgClassName}
      />
    );
  }

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        data-testid={`${testIdPrefix}-trigger`}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label={`Actions for ${targetName || "user"}`}
        className="block tb-tap rounded-full focus:outline-none focus:ring-2 focus:ring-tb-coral hover:ring-2 hover:ring-tb-coral/50"
        title={`Tap for options · ${targetName || ""}`}
      >
        <img
          src={targetAvatar} alt={imgAlt || targetName || ""}
          className={imgClassName + " cursor-pointer"}
        />
      </button>
      {open && (
        <div
          data-testid={`${testIdPrefix}-menu`}
          className={`absolute z-40 top-full mt-1 ${align === "right" ? "right-0" : "left-0"} w-44 rounded-xl bg-tb-cream border-2 border-tb-cream-3 shadow-lg overflow-hidden`}
        >
          <div className="px-3 py-2 border-b border-tb-cream-3 font-hand text-tb-navy/65 text-xs italic truncate">
            {targetName || "user"}
          </div>
          <button
            data-testid={`${testIdPrefix}-message`}
            type="button" onClick={doMessage} disabled={busy}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-hand text-tb-navy hover:bg-tb-cream-2 tb-tap text-left disabled:opacity-50"
          >
            <MessageSquare className="w-4 h-4" /> message
          </button>
          <button
            data-testid={`${testIdPrefix}-report`}
            type="button" onClick={doReport} disabled={busy}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-hand text-tb-navy hover:bg-tb-cream-2 tb-tap text-left disabled:opacity-50"
          >
            <Flag className="w-4 h-4" /> flag for review
          </button>
          <button
            data-testid={`${testIdPrefix}-block`}
            type="button" onClick={doBlock} disabled={busy}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-hand text-tb-coral hover:bg-tb-coral-soft tb-tap text-left disabled:opacity-50 border-t border-tb-cream-3"
          >
            <UserX className="w-4 h-4" /> block
          </button>
          <button
            data-testid={`${testIdPrefix}-close`}
            type="button" onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-hand text-tb-navy/55 hover:bg-tb-cream-2 tb-tap text-left border-t border-tb-cream-3"
          >
            <X className="w-3 h-3" /> cancel
          </button>
        </div>
      )}
    </div>
  );
}
